/**
 * Timeframe Utilities - Shared date filtering logic
 * 
 * Provides consistent timeframe options and date range calculations
 * across all tabs (Jobs, Candidates, Submissions)
 */

/**
 * Standard timeframe options used across the application
 */
export const TIMEFRAME_OPTIONS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'all_time', label: 'All Time' },
  { value: 'ytd', label: 'YTD' }
];

/**
 * Calculate date range based on timeframe selection
 * 
 * @param {string} timeframe - One of: 'this_month', 'last_month', 'last_6_months', 'last_12_months', 'ytd', 'all_time'
 * @returns {Object} { start: Date, end: Date }
 */
export const getDateRangeForTimeframe = (timeframe) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  switch (timeframe) {
    case 'this_month':
      // First day of current month to now
      return {
        start: new Date(currentYear, currentMonth, 1),
        end: now
      };
      
    case 'last_month':
      // First day to last day of previous month
      return {
        start: new Date(currentYear, currentMonth - 1, 1),
        end: new Date(currentYear, currentMonth, 0) // Day 0 = last day of previous month
      };
      
    case 'last_6_months':
      // 6 months ago to now
      const sixMonthsAgo = new Date(now);
      sixMonthsAgo.setMonth(currentMonth - 6);
      return {
        start: sixMonthsAgo,
        end: now
      };
      
    case 'last_12_months':
      // 12 months ago to now
      const twelveMonthsAgo = new Date(now);
      twelveMonthsAgo.setMonth(currentMonth - 12);
      return {
        start: twelveMonthsAgo,
        end: now
      };
      
    case 'ytd':
      // Year to date: Jan 1 of current year to now
      return {
        start: new Date(currentYear, 0, 1),
        end: now
      };
      
    case 'all_time':
      // All historical data (starting from 2000)
      return {
        start: new Date(2000, 0, 1),
        end: now
      };
      
    default:
      // Default to YTD if unknown timeframe
      return {
        start: new Date(currentYear, 0, 1),
        end: now
      };
  }
};

/**
 * Check if a date falls within a timeframe
 * 
 * @param {Date|string} date - Date to check
 * @param {string} timeframe - Timeframe option
 * @returns {boolean} True if date is within timeframe
 */
export const isDateInTimeframe = (date, timeframe) => {
  if (!date) return false;
  
  // Handle 'all_time' special case
  if (timeframe === 'all_time') return true;
  
  const dateObj = date instanceof Date ? date : new Date(date);
  const range = getDateRangeForTimeframe(timeframe);
  
  return dateObj >= range.start && dateObj <= range.end;
};

/**
 * Filter an array of items by date field and timeframe
 * 
 * @param {Array} items - Array of items to filter
 * @param {string} dateField - Name of the date field (e.g., 'date_added', 'created_at')
 * @param {string} timeframe - Timeframe option
 * @param {string} fallbackDateField - Optional fallback date field
 * @returns {Array} Filtered items
 */
export const filterByTimeframe = (items, dateField, timeframe, fallbackDateField = null) => {
  if (!items || items.length === 0) return [];
  
  // All time - no filtering
  if (timeframe === 'all_time') return items;
  
  const range = getDateRangeForTimeframe(timeframe);
  
  return items.filter(item => {
    const date = item[dateField] || (fallbackDateField && item[fallbackDateField]);
    if (!date) return false;
    
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj >= range.start && dateObj <= range.end;
  });
};

/**
 * Get a human-readable description of the timeframe
 * 
 * @param {string} timeframe - Timeframe option
 * @returns {string} Description
 */
export const getTimeframeDescription = (timeframe) => {
  const option = TIMEFRAME_OPTIONS.find(opt => opt.value === timeframe);
  return option ? option.label : 'Unknown';
};

export default {
  TIMEFRAME_OPTIONS,
  getDateRangeForTimeframe,
  isDateInTimeframe,
  filterByTimeframe,
  getTimeframeDescription
};
